import { clearDashboardQueryCache } from '../api/dashboardQuery';
import { clearRoleProfile } from '../hooks/useRoleProfile';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  clearTokens,
  fetchUserProfile,
  getAccessToken,
  getRefreshToken,
  getTokenExpiresAt,
  isAuthenticated,
  refreshSession,
  redirectToSignOut,
  storeTokens,
} from './authService';
import type { UserProfile } from './authService';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AuthContextValue {
  /** True when a valid (non-expired) access token is stored. */
  authenticated: boolean;
  /** True while the initial auth check is in progress. */
  loading: boolean;
  /** The raw access token string, or null if not authenticated. */
  accessToken: string | null;
  /** The current user's profile, or null while loading / unauthenticated. */
  user: UserProfile | null;
  /**
   * Store the tokens returned by the SSO callback and mark the session as
   * authenticated. Called by `SSOCallback` after a successful redirect.
   */
  signIn: (accessToken: string, refreshToken: string, expiresIn: number, idToken?: string) => void;
  /**
   * Revoke the current token on the backend (best-effort) and clear local
   * state. React Router will redirect to onboarding automatically.
   */
  signOut: () => void;
}

function clearSessionCaches(): void {
  clearRoleProfile();
  clearDashboardQueryCache();
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [authenticated, setAuthenticated] = useState<boolean>(() => isAuthenticated());
  const [accessToken, setAccessToken] = useState<string | null>(() => getAccessToken());
  const [user, setUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Schedule proactive token refresh 60 seconds before expiry
  const scheduleRefresh = useCallback(() => {
    if (refreshTimer.current) clearTimeout(refreshTimer.current);

    const expiresAt = getTokenExpiresAt();
    if (!expiresAt) return;

    const msUntilExpiry = expiresAt - Date.now();
    const refreshIn = Math.max(msUntilExpiry - 60_000, 0);

    refreshTimer.current = setTimeout(async () => {
      const result = await refreshSession();
      if (result === 'refreshed') {
        setAccessToken(getAccessToken());
        setAuthenticated(true);
        scheduleRefresh(); // schedule the next refresh
      } else if (result === 'invalid' || result === 'missing') {
        clearTokens();
        setAuthenticated(false);
        setAccessToken(null);
        setUser(null);
      } else {
        // Transient network/5xx failure: preserve the refresh family and retry.
        refreshTimer.current = setTimeout(scheduleRefresh, 30_000);
      }
    }, refreshIn);
  }, []);

  // Fetch user profile
  const loadUserProfile = useCallback(async () => {
    const profile = await fetchUserProfile();
    if (profile) setUser(profile);
  }, []);

  // On mount: recover from an expired/missing access token when a refresh
  // token still exists, then keep React state synchronized across tabs.
  useEffect(() => {
    let active = true;
    let bootstrapRetryTimer: ReturnType<typeof setTimeout> | null = null;

    const applyStoredSession = async () => {
      let ready = isAuthenticated();
      if (!ready && getRefreshToken()) {
        const result = await refreshSession();
        if (!active) return;
        if (result === 'unavailable') {
          setLoading(true);
          bootstrapRetryTimer = setTimeout(() => { void applyStoredSession(); }, 30_000);
          return;
        }
        ready = result === 'refreshed';
        if (result === 'invalid' || result === 'missing') clearTokens();
      }

      if (!active) return;
      setAuthenticated(ready);
      setAccessToken(ready ? getAccessToken() : null);
      if (ready) {
        await loadUserProfile();
        if (active) scheduleRefresh();
      } else {
        setUser(null);
        clearSessionCaches();
      }
      if (active) setLoading(false);
    };

    void applyStoredSession();

    let storageSyncTimer: ReturnType<typeof setTimeout> | null = null;
    const handleStorage = (event: StorageEvent) => {
      if (!event.key || !['access_token', 'refresh_token', 'token_expires_at', 'id_token'].includes(event.key)) {
        return;
      }
      if (storageSyncTimer) clearTimeout(storageSyncTimer);
      storageSyncTimer = setTimeout(() => { void applyStoredSession(); }, 0);
    };
    window.addEventListener('storage', handleStorage);

    return () => {
      active = false;
      window.removeEventListener('storage', handleStorage);
      if (storageSyncTimer) clearTimeout(storageSyncTimer);
      if (bootstrapRetryTimer) clearTimeout(bootstrapRetryTimer);
      if (refreshTimer.current) clearTimeout(refreshTimer.current);
    };
  }, [loadUserProfile, scheduleRefresh]);

  const signIn = useCallback(
    (access: string, refresh: string, expiresIn: number, idToken?: string) => {
      storeTokens(access, refresh, expiresIn, idToken);
      setAccessToken(access);
      setAuthenticated(true);
      scheduleRefresh();
      // Fetch user profile after tokens are stored
      fetchUserProfile().then((profile) => {
        if (profile) setUser(profile);
      });
    },
    [scheduleRefresh],
  );

  const signOut = useCallback(() => {
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
    clearSessionCaches();

    // NO setState BEFORE THE REDIRECT. This used to call setAuthenticated(false) /
    // setAccessToken(null) / setUser(null) first, and that made sign-out need TWO
    // clicks — reproduced from the production audit log on 2026-08-03:
    //
    //   05:47:29  USER_SIGN_OUT  {token_identified: False}   <- only the RFC 7009
    //                                                          revoke; NO
    //                                                          `method: sign_out` row
    //   05:47:33  USER_SIGN_IN   flow=authorization_code     <- signed straight back in
    //   05:47:41  USER_SIGN_OUT  session_destroyed: True     <- the second click
    //
    // The first click never reached `GET /sign-out`. Flipping `authenticated` to false
    // re-renders, both guards in App.tsx immediately return
    // `<Navigate to="/onboarding" replace />`, and that same-document navigation
    // cancels the cross-origin `window.location.replace` queued a line earlier. The
    // session cookie therefore survived, `/onboarding`'s silent SSO succeeded, and the
    // user was back inside four seconds later.
    //
    // Local state is meaningless here: the document is about to be discarded. So the
    // redirect goes first and nothing competes with it.
    redirectToSignOut();
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ authenticated, loading, accessToken, user, signIn, signOut }),
    [authenticated, loading, accessToken, user, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
